import clsx from 'clsx'
import {useFormik} from 'formik'
import {find, forEach, includes, omit} from 'lodash'
import React, {useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import {
  Button,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import {AutocompleteAddress} from '../../../../common'
import {ACCOUNT_PROFILE_FIELD} from '../../../core/_const'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {toast} from 'react-toastify'
import SettingsService from '../../../core/_requests'
import UserService from '../../../../users/core/_requests'
import {useAuth} from '../../../../auth'

type Props = {
  show: boolean
  handleClose: () => void
}

const AccountProfileEditModal: React.FC<Props> = ({show, handleClose}) => {
  const intl = useIntl()
  const {handleSaveCurrentUser, currentUser} = useAuth()
  const {stringYup, regexYup} = UseYupValidate()
  const {isCancel, newCancelToken} = useCancelToken()
  const [validationErrors, setValidationErrors] = useState<any>()

  const validateSchema = useMemo(() => {
    return {
      name: stringYup(255, 'ACCOUNT_NAME', true),
      street1: stringYup(100, 'ADDRESS', true),
      street2: stringYup(100, 'ADDRESS_2', false),
      country: stringYup(50, 'COUNTRY', true),
      city: stringYup(100, 'CITY', true),
      zip: Yup.string().when('country', {
        is: (val) => val === 'US',
        then: regexYup.zipCode('POSTAL_CODE', true),
        otherwise: stringYup(10, 'POSTAL_CODE', true),
      }),
      state: Yup.string().when('country', {
        is: (val) => val === 'US',
        then: regexYup.state('STATE', true),
        otherwise: stringYup(50, 'STATE', true),
      }),
      phone: stringYup(20, 'PHONE', false),
      shipper_code: stringYup(50, 'SHIPPER_CODE', false),
    }
  }, [stringYup, regexYup])

  const initialValues = useMemo(() => {
    const account = currentUser?.current_account
    let init: any = {}
    forEach(ACCOUNT_PROFILE_FIELD, (field: any) => {
      init[field.name] = account?.[field.name] ?? ''
    })
    return init
  }, [currentUser])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object().shape(validateSchema),
    onSubmit: async (values, {setSubmitting}) => {
      try {
        setSubmitting(true)
        const payload = {
          ...omit(values, ['name']),
          account_name: values.name,
          type: 'account',
        }
        const config = {cancelToken: newCancelToken()}
        const res = await SettingsService.accountEdit(payload, config)
        if (res) {
          const user = await UserService.getUser(config)
          handleSaveCurrentUser(user)
        }
        toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
        handleClose()
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleCloseModal = () => {
    handleClose()
    formik.resetForm()
  }

  const selectedPlace = (selected) => {
    let city = find(selected.address_components, (item) => {
      return includes(item?.types, 'locality')
    })
    formik.setFieldValue('city', city?.long_name ?? '')
    let state = find(selected.address_components, (item) => {
      return includes(item?.types, 'administrative_area_level_1')
    })
    formik.setFieldValue('state', state?.short_name ?? '')
    let country = find(selected.address_components, (item) => {
      return includes(item?.types, 'country')
    })
    formik.setFieldValue('country', country?.short_name ?? '')
    let zipCode = find(selected.address_components, (item) => {
      return includes(item?.types, 'postal_code')
    })
    let suffix = find(selected.address_components, (item) => {
      return includes(item?.types, 'postal_code_suffix')
    })
    if (zipCode?.long_name) {
      zipCode = zipCode?.long_name + (suffix?.long_name ? `-${suffix?.long_name}` : '')
    } else {
      zipCode = ''
    }
    formik.setFieldValue('zip', zipCode)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      <Modal
        id='gori_modal_account_profile_edit'
        tabIndex={-1}
        centered
        dialogClassName='mw-650px h-auto'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'ACCOUNT_PROFILE'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='scroll-y'>
            <div className='row px-10'>
              {ACCOUNT_PROFILE_FIELD.map((field, index) => {
                if (field.type === 'text') {
                  return (
                    <div className={`my-2 ${field?.col || 'col-12'}`} key={index}>
                      <InputTextFormik
                        className='col-12'
                        formik={formik}
                        name={field.name}
                        disabled={formik.isSubmitting}
                        label={intl.formatMessage({id: field.label})}
                        labelClassName={clsx('fw-bolder', {required: field.required})}
                      />
                    </div>
                  )
                }
                if (field.type === 'select') {
                  return (
                    <div className='my-2 col-12' key={index}>
                      <SelectFormik
                        checkFormik
                        placeholder={''}
                        options={field?.options || []}
                        formik={formik}
                        name={field.name}
                        required={field.required}
                        label={intl.formatMessage({id: field.label})}
                        labelClassName={clsx('fw-bolder', {required: field.required})}
                      />
                    </div>
                  )
                }
                if (field.type === 'address') {
                  return (
                    <div className={`my-2 ${field?.col || 'col-12'} address-complete`} key={index}>
                      <label className={`form-label fw-bolder ${field.required ? 'required' : ''}`}>
                        {intl.formatMessage({id: field.label})}
                      </label>
                      <AutocompleteAddress
                        formik={formik}
                        name={field.name}
                        label=''
                        onSelected={(selected) => {
                          selectedPlace(selected)
                        }}
                      />
                    </div>
                  )
                }
                return null
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-advanced-primary'
                label={intl.formatMessage({id: 'SAVE'})}
                loadingText={intl.formatMessage({id: 'SAVE'})}
                disabled={formik.isSubmitting}
                event={formik.submitForm}
                loading={formik.isSubmitting}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {AccountProfileEditModal}
